import { z } from "zod";
import { GraphSchema } from "./actions.ts";
import type { EntityKind } from "./refs.ts";
import { TokenRefSchema } from "./tokenRef.ts";

const MatchmakerAllowedTeamSizesSchema = z
	.array(z.number().finite().int().positive("allowedTeamSizes entries must be > 0"))
	.min(1, "allowedTeamSizes must contain at least one size");

export const ItemSchema = z
	.object({
		id: z.string().min(1),
		name: z.string().min(1, "Missing REQUIRED name"),
		modelPaths: z.array(z.string()).nullable().optional(),
		category: z.string().nullable().optional(),
		maxQuantity: z
			.number()
			.finite()
			.int()
			.positive("maxQuantity must be > 0")
			.nullable()
			.optional(),
		wanderRadius: z
			.number()
			.finite()
			.positive("wanderRadius must be > 0")
			.nullable()
			.optional(),
		stats: z.record(z.string().min(1), z.number().finite()).nullable().optional(),
		attacks: z.array(z.string()).nullable().optional(),
		graph: GraphSchema.nullable().optional(),
		minTeamsToStart: z
			.number()
			.finite()
			.int()
			.positive("minTeamsToStart must be > 0")
			.nullable()
			.optional(),
		maxTeams: z
			.number()
			.finite()
			.int()
			.positive("maxTeams must be > 0")
			.nullable()
			.optional(),
		allowedTeamSizes: MatchmakerAllowedTeamSizesSchema.nullable().optional(),
		countdownSeconds: z
			.number()
			.finite()
			.positive("countdownSeconds must be > 0")
			.nullable()
			.optional(),
		matchQuest: z.string().min(1).nullable().optional(),
		folderId: z.string().nullable().optional(),
	})
	.strict()
	.superRefine((val, ctx) => {
		if (val.category !== "Matchmaker") return;
		if (val.minTeamsToStart === null || val.minTeamsToStart === undefined) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["minTeamsToStart"],
				message: "minTeamsToStart required for Matchmaker items",
			});
		}
		if (val.maxTeams === null || val.maxTeams === undefined) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["maxTeams"],
				message: "maxTeams required for Matchmaker items",
			});
		}
		if (val.allowedTeamSizes === null || val.allowedTeamSizes === undefined) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["allowedTeamSizes"],
				message: "allowedTeamSizes required for Matchmaker items",
			});
		}
		if (val.countdownSeconds === null || val.countdownSeconds === undefined) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["countdownSeconds"],
				message: "countdownSeconds required for Matchmaker items",
			});
		}
		if (
			val.minTeamsToStart !== null &&
			val.minTeamsToStart !== undefined &&
			val.maxTeams !== null &&
			val.maxTeams !== undefined &&
			val.maxTeams < val.minTeamsToStart
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["maxTeams"],
				message: "maxTeams must be >= minTeamsToStart",
			});
		}
		if (val.allowedTeamSizes) {
			const seen = new Set<number>();
			for (const [index, size] of val.allowedTeamSizes.entries()) {
				if (seen.has(size)) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						path: ["allowedTeamSizes", index],
						message: "allowedTeamSizes must not contain duplicates",
					});
				}
				seen.add(size);
			}
		}
	});
export type Item = z.infer<typeof ItemSchema>;

export const RangeTier = z.enum(["Melee", "Medium", "Long"]);
export const AoeTier = z.enum(["Small", "Medium", "Large"]);
export const DamageType = z.enum(["absolute", "percent"]);

export const DamageEntrySchema = z.object({
	id: z.string().optional(),
	statKey: z.string().min(1, "statKey required"),
	amount: z.number().finite().positive("amount must be > 0"),
	type: DamageType,
}).strict();
export type DamageEntry = z.infer<typeof DamageEntrySchema>;

export const AttackSchema = z
	.object({
		id: z.string().min(1),
		name: z.string().min(1),
		range: RangeTier,
		cooldown: z.number().finite().positive("cooldown must be > 0"),
		animationId: z.string().nullable().optional(),
		vfxModelPath: z.string().nullable().optional(),
		aoeRadius: AoeTier.nullable().optional(),
		costStat: z.string().nullable().optional(),
		costAmount: z.number().finite().nonnegative().nullable().optional(),
		damage: z.array(DamageEntrySchema).min(1, "At least one damage entry required"),
		hitStunDuration: z.number().finite().nonnegative().nullable().optional(),
		swingSoundId: z.string().nullable().optional(),
		impactSoundId: z.string().nullable().optional(),
		screenShakeIntensity: z.number().finite().nonnegative().nullable().optional(),
		projectileModelPath: z.string().nullable().optional(),
		projectileSpeed: z.number().finite().positive("projectileSpeed must be > 0").nullable().optional(),
		projectileMaxDistance: z.number().finite().positive("projectileMaxDistance must be > 0").nullable().optional(),
		projectileHoming: z.boolean().nullable().optional(),
		projectilePiercing: z.boolean().nullable().optional(),
		folderId: z.string().nullable().optional(),
	})
	.strict()
	.superRefine((val, ctx) => {
		const hasCostStat = val.costStat !== null && val.costStat !== undefined && val.costStat !== "";
		const hasCostAmount = val.costAmount !== null && val.costAmount !== undefined;
		if (hasCostStat && !hasCostAmount) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["costAmount"],
				message: "costAmount required when costStat is set",
			});
		}
		if (hasCostAmount && !hasCostStat) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["costStat"],
				message: "costStat required when costAmount is set",
			});
		}
		const hasProjectile =
			val.projectileModelPath !== null &&
			val.projectileModelPath !== undefined &&
			val.projectileModelPath !== "";
		if (hasProjectile) {
			if (val.projectileSpeed === null || val.projectileSpeed === undefined) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["projectileSpeed"],
					message: "projectileSpeed required when projectileModelPath is set",
				});
			}
			if (val.projectileMaxDistance === null || val.projectileMaxDistance === undefined) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["projectileMaxDistance"],
					message: "projectileMaxDistance required when projectileModelPath is set",
				});
			}
		}
	});
export type Attack = z.infer<typeof AttackSchema>;

export const RecipeEntrySchema = z.discriminatedUnion("type", [
	z
		.object({
			type: z.literal("item"),
			itemId: z.string().min(1, "itemId required"),
			quantity: z.number().finite().int().positive("quantity must be > 0"),
		})
		.strict(),
	z
		.object({
			type: z.literal("currency"),
			currencyId: z.string().min(1, "currencyId required"),
			amount: z.number().finite().positive("amount must be > 0"),
		})
		.strict(),
]);
export type RecipeEntry = z.infer<typeof RecipeEntrySchema>;

export const QuestStatRewardMode = z.enum([
	"Set",
	"Increase",
	"Decrease",
	"Restore",
]);

export const QuestRewardSchema = z.discriminatedUnion("type", [
	z
		.object({
			type: z.literal("item"),
			itemId: z.string().min(1, "itemId required"),
			quantity: z.number().finite().int().positive("quantity must be > 0"),
		})
		.strict(),
	z
		.object({
			type: z.literal("currency"),
			currencyId: z.string().min(1, "currencyId required"),
			amount: z.number().finite().positive("amount must be > 0"),
		})
		.strict(),
	z
		.object({
			type: z.literal("stat"),
			statKey: z.string().min(1, "statKey required"),
			mode: QuestStatRewardMode,
			value: z.string().min(1, "value required"),
		})
		.strict(),
]);
export type QuestReward = z.infer<typeof QuestRewardSchema>;

export const RecipeSchema = z
	.object({
		id: z.string().min(1),
		name: z.string().min(1),
		inputs: z.array(RecipeEntrySchema).default([]),
		outputs: z.array(RecipeEntrySchema).min(1, "At least one output required"),
		developerProductId: z.string().nullable().optional(),
		description: z.string().nullable().optional(),
	})
	.strict()
	.superRefine((val, ctx) => {
		const hasDevProduct =
			val.developerProductId !== null &&
			val.developerProductId !== undefined &&
			val.developerProductId !== "";
		if (val.inputs.length === 0 && !hasDevProduct) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["inputs"],
				message: "Must have at least one input (or set developerProductId)",
			});
		}
	});
export type Recipe = z.infer<typeof RecipeSchema>;

export const CurrencySchema = z
	.object({
		id: z.string().min(1),
		name: z.string().min(1, "Missing REQUIRED name"),
		modelPath: z.string().nullable().optional(),
		order: z.number().finite(),
		passiveRatePerHour: z.number().finite().nonnegative().nullable().optional(),
		maxAmount: z.number().finite().positive().nullable().optional(),
	})
	.strict();
export type Currency = z.infer<typeof CurrencySchema>;

export const StatImpact = z.enum(["Attack", "Defense", "Vitality", "Speed", "Production", "None"]);
export const StatDisplay = z.enum(["hidden", "bar", "tracker"]);

const ProgressionThresholdSchema = z.object({
	required: z.number().finite().nonnegative(),
	baseStats: z.record(z.string().min(1), z.number().finite()),
}).strict();

const ProgressionSchema = z.object({
	targetStat: z.string().min(1),
	thresholds: z.array(ProgressionThresholdSchema).min(1),
}).strict();

export const StatSchema = z
	.object({
		id: z.string().min(1),
		name: z.string().min(1),
		order: z.number().finite(),
		showInUi: z.boolean().default(true),
		display: StatDisplay,
		impact: StatImpact,
		iconAssetId: z.string().nullable().optional(),
		progression: ProgressionSchema.nullable().optional(),
		showTierChangeMessage: z.boolean().default(true),
		regenPerSecond: z.number().finite().nonnegative().nullable().optional(),
	})
	.strict();
export type Stat = z.infer<typeof StatSchema>;

export const QuestAction = z.enum([
	"Kill",
	"Consume",
	"Craft",
	"Collect",
	"ReachStat",
	"Die",
]);
export type QuestAction = z.infer<typeof QuestAction>;
export const QuestType = z.enum(["Journal", "Daily", "Other", "Match"]);

export const QuestObjectiveSchema = z
	.object({
		action: QuestAction,
		itemId: z.string().min(1, "itemId required"),
		amount: z.union([
			z.number().finite().int().positive("amount must be > 0"),
			z.enum(["one", "all"]),
		]),
	})
	.strict()
	.superRefine((val, ctx) => {
		if (val.action === "Die") {
			if (val.amount !== "one" && val.amount !== "all") {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["amount"],
					message: "Die objectives require amount 'one' or 'all'",
				});
			}
			return;
		}
		if (typeof val.amount !== "number") {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["amount"],
				message: "Non-Die objectives require a numeric amount",
			});
		}
	});
export type QuestObjective = z.infer<typeof QuestObjectiveSchema>;

export const QuestSchema = z
	.object({
		id: z.string().min(1),
		name: z.string().min(1),
		description: z.string().nullable().optional(),
		type: QuestType,
		winObjectives: z
			.array(QuestObjectiveSchema)
			.min(1, "At least one win objective required"),
		loseObjectives: z.array(QuestObjectiveSchema).default([]),
		rewards: z.array(QuestRewardSchema).min(1, "At least one reward required"),
		order: z.number().finite().default(0),
		timeLimit: z
			.number()
			.finite()
			.positive("timeLimit must be > 0")
			.nullable()
			.optional(),
		repeatable: z.boolean().optional(),
	})
	.strict()
	.superRefine((val, ctx) => {
		if (val.type === "Match" && val.loseObjectives.length === 0) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["loseObjectives"],
				message: "Match quests require at least one lose objective",
			});
		}
	});
export type Quest = z.infer<typeof QuestSchema>;

export const PlaceKindSchema = z.enum([
	"HiddenAssets",
	"PrivatePlot",
	"PublicPlot",
	"ClaimablePlace",
]);
export type PlaceKind = z.infer<typeof PlaceKindSchema>;

const PLACE_PATH_REGEX = /^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)*$/;

export const PlaceSchema = z
	.object({
		id: z.string().min(1),
		kind: PlaceKindSchema,
		label: z
			.string()
			.min(1, "Missing REQUIRED label")
			.max(128, "label must be at most 128 characters"),
		path: z
			.string()
			.min(1, "Missing REQUIRED path")
			.max(512, "path must be at most 512 characters")
			.regex(
				PLACE_PATH_REGEX,
				"path must be dot-separated identifiers (e.g. Workspace.Assets.Foo)",
			),
	})
	.strict();
export type Place = z.infer<typeof PlaceSchema>;

export const CmsPlaceCreateBody = PlaceSchema.omit({ id: true });
export type CmsPlaceCreate = z.infer<typeof CmsPlaceCreateBody>;

export const CmsPlaceUpdateBody = PlaceSchema.partial().omit({ id: true });
export type CmsPlaceUpdate = z.infer<typeof CmsPlaceUpdateBody>;

export const UiNodeType = z.enum([
	"Frame",
	"ForEach",
	"Text",
	"Image",
	"Viewport",
	"Repeater",
	"UICorner",
	"UIStroke",
	"UIPadding",
	"UIListLayout",
	"UIGridLayout",
	"UIGradient",
]);
export type UiNodeType = z.infer<typeof UiNodeType>;

export const UiTextXAlignment = z.enum(["Left", "Center", "Right"]);
export const UiTextYAlignment = z.enum(["Top", "Center", "Bottom"]);
export const UiTextTruncate = z.enum(["None", "AtEnd"]);
export const UiFont = z.enum([
	"Gotham",
	"GothamBold",
	"GothamMedium",
	"GothamSemibold",
	"GothamBlack",
	"SourceSans",
	"SourceSansBold",
]);

export const UiColorSchema = z
	.object({
		r: z.number().int().min(0).max(255),
		g: z.number().int().min(0).max(255),
		b: z.number().int().min(0).max(255),
	})
	.strict();
export type UiColor = z.infer<typeof UiColorSchema>;

export const UiColorOrToken = z.union([UiColorSchema, TokenRefSchema]);
export type UiColorOrToken = z.infer<typeof UiColorOrToken>;

export const UiFontOrToken = z.union([UiFont, TokenRefSchema]);
export type UiFontOrToken = z.infer<typeof UiFontOrToken>;

export const UDimSchema = z
	.object({
		scale: z.number().finite(),
		offset: z.number().finite(),
	})
	.strict();
export type UDim = z.infer<typeof UDimSchema>;

export const UDim2Schema = z
	.object({
		scaleX: z.number().finite(),
		offsetX: z.number().finite(),
		scaleY: z.number().finite(),
		offsetY: z.number().finite(),
	})
	.strict();
export type UDim2 = z.infer<typeof UDim2Schema>;

export const UiEdgeValueSchema = z.union([
	z.number().finite(),
	z
		.object({
			pct: z.number().finite().optional(),
			px: z.number().finite().optional(),
		})
		.strict(),
]);
export type UiEdgeValue = z.infer<typeof UiEdgeValueSchema>;

export const Vector2Schema = z
	.object({
		x: z.number().finite().min(0).max(1),
		y: z.number().finite().min(0).max(1),
	})
	.strict();
export type Vector2 = z.infer<typeof Vector2Schema>;

export const AutomaticSizeSchema = z.enum(["None", "X", "Y", "XY"]);

const RobloxCommonProps = {
	Size: UDim2Schema.optional(),
	Position: UDim2Schema.optional(),
	AnchorPoint: Vector2Schema.optional(),
	left: UiEdgeValueSchema.optional(),
	right: UiEdgeValueSchema.optional(),
	top: UiEdgeValueSchema.optional(),
	bottom: UiEdgeValueSchema.optional(),
	width: UiEdgeValueSchema.optional(),
	height: UiEdgeValueSchema.optional(),
	AutomaticSize: AutomaticSizeSchema.default("None"),
	BackgroundColor3: UiColorOrToken.nullable().optional(),
	BackgroundTransparency: z.number().min(0).max(1).default(0),
	BorderSizePixel: z.number().int().min(0).default(0),
	ClipsDescendants: z.boolean().default(false),
	Visible: z.boolean().default(true),
	ZIndex: z.number().int().default(1),
};

export const UiFrameProps = z.object(RobloxCommonProps).strict();
export type UiFrameProps = z.infer<typeof UiFrameProps>;

export const UiTextProps = z
	.object({
		...RobloxCommonProps,
		Text: z.string().default(""),
		TextColor3: UiColorOrToken.default({ r: 255, g: 255, b: 255 }),
		Font: UiFontOrToken.default("Gotham"),
		TextSize: z.number().min(1).max(200).default(14),
		TextXAlignment: UiTextXAlignment.default("Left"),
		TextYAlignment: UiTextYAlignment.default("Center"),
		TextWrapped: z.boolean().default(false),
		TextTruncate: UiTextTruncate.default("None"),
		TextScaled: z.boolean().default(false),
		TextTransparency: z.number().min(0).max(1).default(0),
	})
	.strict();
export type UiTextProps = z.infer<typeof UiTextProps>;

export const UiImageProps = z
	.object({
		...RobloxCommonProps,
		Image: z.string().default(""),
		ImageColor3: UiColorOrToken.default({ r: 255, g: 255, b: 255 }),
		ImageTransparency: z.number().min(0).max(1).default(0),
	})
	.strict();
export type UiImageProps = z.infer<typeof UiImageProps>;

export const UiViewportProps = z
	.object({
		...RobloxCommonProps,
		itemId: z.string().default(""),
	})
	.strict();
export type UiViewportProps = z.infer<typeof UiViewportProps>;


const RepeaterQueryFilterSchema = z.object({
	field: z.string().min(1),
	op: z.enum(["containsItem", "containsCurrency", "isSet", "eq"]),
	value: z.unknown().optional(),
}).strict();

const RepeaterQuerySchema = z.object({
	source: z.string().min(1),
	arg: z.string().optional(),
	output: z.literal("items").default("items"),
	filters: z.array(RepeaterQueryFilterSchema).optional(),
}).strict();

export const UiRepeaterProps = z
	.object({
		...RobloxCommonProps,
		query: RepeaterQuerySchema,
	})
	.strict();
export type UiRepeaterProps = z.infer<typeof UiRepeaterProps>;

export const UICornerProps = z
	.object({
		CornerRadius: UDimSchema.default({ scale: 0, offset: 0 }),
	})
	.strict();
export type UICornerProps = z.infer<typeof UICornerProps>;

export const UIStrokeProps = z
	.object({
		Color: UiColorOrToken.default({ r: 0, g: 0, b: 0 }),
		Thickness: z.number().min(0).max(10).default(1),
		Transparency: z.number().min(0).max(1).default(0),
		ApplyStrokeMode: z.enum(["Contextual", "Border"]).default("Contextual"),
	})
	.strict();
export type UIStrokeProps = z.infer<typeof UIStrokeProps>;

export const UIPaddingProps = z
	.object({
		PaddingTop: UDimSchema.default({ scale: 0, offset: 0 }),
		PaddingRight: UDimSchema.default({ scale: 0, offset: 0 }),
		PaddingBottom: UDimSchema.default({ scale: 0, offset: 0 }),
		PaddingLeft: UDimSchema.default({ scale: 0, offset: 0 }),
	})
	.strict();
export type UIPaddingProps = z.infer<typeof UIPaddingProps>;

export const UIListLayoutDirection = z.enum(["Vertical", "Horizontal"]);
export const UIListLayoutAlignment = z.enum(["Left", "Center", "Right", "Top", "Bottom"]);
export const UIListLayoutSortOrder = z.enum(["LayoutOrder", "Name"]);

export const UIListLayoutProps = z
	.object({
		FillDirection: UIListLayoutDirection.default("Vertical"),
		Padding: UDimSchema.default({ scale: 0, offset: 0 }),
		HorizontalAlignment: z.enum(["Left", "Center", "Right"]).default("Left"),
		VerticalAlignment: z.enum(["Top", "Center", "Bottom"]).default("Top"),
		SortOrder: UIListLayoutSortOrder.default("LayoutOrder"),
		Wraps: z.boolean().default(false),
	})
	.strict();
export type UIListLayoutProps = z.infer<typeof UIListLayoutProps>;

export const UIGridLayoutProps = z
	.object({
		CellSize: UDim2Schema.default({ scaleX: 0, offsetX: 64, scaleY: 0, offsetY: 64 }),
		CellPadding: UDim2Schema.default({ scaleX: 0, offsetX: 4, scaleY: 0, offsetY: 4 }),
		FillDirection: z.enum(["Horizontal", "Vertical"]).default("Horizontal"),
		HorizontalAlignment: z.enum(["Left", "Center", "Right"]).default("Left"),
		VerticalAlignment: z.enum(["Top", "Center", "Bottom"]).default("Top"),
		SortOrder: UIListLayoutSortOrder.default("LayoutOrder"),
		StartCorner: z.enum(["TopLeft", "TopRight", "BottomLeft", "BottomRight"]).default("TopLeft"),
	})
	.strict();
export type UIGridLayoutProps = z.infer<typeof UIGridLayoutProps>;

export const UIGradientStopSchema = z
	.object({
		color: UiColorOrToken,
		position: z.number().min(0).max(1),
	})
	.strict();
export type UIGradientStop = z.infer<typeof UIGradientStopSchema>;

export const UIGradientProps = z
	.object({
		Color: z.array(UIGradientStopSchema).min(2).default([
			{ color: { r: 255, g: 255, b: 255 }, position: 0 },
			{ color: { r: 0, g: 0, b: 0 }, position: 1 },
		]),
		Rotation: z.number().min(-360).max(360).default(0),
		Transparency: z
			.array(
				z
					.object({
						position: z.number().min(0).max(1),
						value: z.number().min(0).max(1),
					})
					.strict(),
			)
			.optional(),
	})
	.strict();
export type UIGradientProps = z.infer<typeof UIGradientProps>;

export type UiNode = {
	id: string;
	type: UiNodeType;
	name: string;
	props:
		| UiFrameProps
		| UiTextProps
		| UiImageProps
		| UiViewportProps
		| UiRepeaterProps
		| UICornerProps
		| UIStrokeProps
		| UIPaddingProps
		| UIListLayoutProps
		| UIGridLayoutProps
		| UIGradientProps;
	children: UiNode[];
	graph?: z.infer<typeof GraphSchema>;
};

const MODIFIER_TYPES = new Set<UiNodeType>([
	"UICorner",
	"UIStroke",
	"UIPadding",
	"UIListLayout",
	"UIGridLayout",
	"UIGradient",
]);

export const UiNodeSchema: z.ZodType<UiNode> = z.lazy(() =>
	z
		.object({
			id: z.string().min(1),
			type: UiNodeType,
			name: z.string().min(1),
			props: z.union([
				UiFrameProps,
				UiTextProps,
				UiImageProps,
				UiViewportProps,
				UiRepeaterProps,
				UICornerProps,
				UIStrokeProps,
				UIPaddingProps,
				UIListLayoutProps,
				UIGridLayoutProps,
				UIGradientProps,
			]),
			children: z.array(UiNodeSchema).default([]),
			graph: GraphSchema.optional(),
		})
		.strict()
		.superRefine((node, ctx) => {
			if (node.type === "Repeater" && node.children.length !== 1) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["children"],
					message: `Repeater requires exactly 1 child as template (got ${node.children.length})`,
				});
			}
			if (MODIFIER_TYPES.has(node.type) && node.children.length !== 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["children"],
					message: `${node.type} is a modifier (Instance attached to parent); it must not have children`,
				});
			}
		}) as unknown as z.ZodType<UiNode>,
);

export const UI_TREE_ROLES = [
	"",
	"Dialogue",
	"Shop",
	"RobuxShop",
	"FreeMode",
	"Notification",
	"Choice",
	"Title",
	"HUD",
	"Inventory",
	"Quest",
	"CheckoutQuest",
	"TargetSelector",
	"DamagePopup",
	"RewardPopup",
	"Splash",
	"LevelUpFX",
	"DailyQuest",
	"ActiveTimers",
	"Goal",
	"TeamPlayers",
	"Team",
] as const;
export type UiTreeRole = (typeof UI_TREE_ROLES)[number];
export const UiTreeRoleSchema = z.enum(UI_TREE_ROLES);

export const UiTreeSchema = z
	.object({
		id: z.string().min(1),
		name: z
			.string()
			.min(1, "Missing REQUIRED name")
			.max(255, "name must be at most 255 characters"),
		role: UiTreeRoleSchema.default(""),
		root: UiNodeSchema,
	})
	.strict();
export type UiTree = z.infer<typeof UiTreeSchema>;

export const CmsUiTreeCreateBody = UiTreeSchema.omit({ id: true });
export type CmsUiTreeCreate = z.infer<typeof CmsUiTreeCreateBody>;

export const CmsUiTreeUpdateBody = UiTreeSchema.partial().omit({ id: true });
export type CmsUiTreeUpdate = z.infer<typeof CmsUiTreeUpdateBody>;

export const ProjectViewSchema = z
	.object({
		items: z.record(z.string().min(1), ItemSchema),
		attacks: z.record(z.string().min(1), AttackSchema),
		recipes: z.record(z.string().min(1), RecipeSchema),
		currencies: z.record(z.string().min(1), CurrencySchema),
		stats: z.record(z.string().min(1), StatSchema),
		quests: z.record(z.string().min(1), QuestSchema),
		places: z.record(z.string().min(1), PlaceSchema).default({}),
		uiTrees: z.record(z.string().min(1), UiTreeSchema).default({}),
	})
	.strict()
	.superRefine((view, ctx) => {
		const records = {
			items: view.items,
			attacks: view.attacks,
			recipes: view.recipes,
			currencies: view.currencies,
			stats: view.stats,
			quests: view.quests,
			places: view.places,
			uiTrees: view.uiTrees,
		};
		for (const [kind, entities] of Object.entries(records)) {
			for (const [id, entity] of Object.entries(entities)) {
				if (entity.id !== id) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						path: [kind, id, "id"],
						message: `${kind}["${id}"].id must equal "${id}"`,
					});
				}
			}
		}
	});
export type ProjectView = z.infer<typeof ProjectViewSchema>;

export const PLUGIN_OWNED_FIELDS: Readonly<
	Record<EntityKind, readonly string[]>
> = Object.freeze({
	items: ["modelPaths"],
	attacks: [
		"vfxModelPath",
		"projectileModelPath",
		"animationId",
		"swingSoundId",
		"impactSoundId",
	],
	recipes: [],
	currencies: ["modelPath"],
	stats: ["iconAssetId"],
	quests: [],
	uiTrees: [],
});

export const PLUGIN_OWNED_GRAPH_FIELDS: Readonly<
	Record<string, readonly string[]>
> = Object.freeze({
	PlaySound: ["soundPath"],
	PlayAnimation: ["animationPath"],
	Teleport: ["destination"],
});
